import { Feature, FeatureCollection, GeoJsonProperties, MultiPolygon, Polygon, Position } from "geojson";
import { t } from "i18next";
import { ExpressionSpecification, ResourceType, StyleSpecification } from "maplibre-gl";
import pointInPolygon from "point-in-polygon";
import { useMemo, useState } from "react";
import { Badge, ButtonGroup, Dropdown, DropdownButton } from "react-bootstrap";
import { useTerritories, useTerritoryRecords } from "../../../api/territory";
import { territoryAddressCompare } from "../../../helpers/address";
import { cachedPolygonCenter, getPolygon } from "../../../helpers/geo";
import HourglassGlobals from "../../../helpers/globals";
import { CollatorSingleton, containsFuzzy } from "../../../helpers/locale";
import { userCompare } from "../../../helpers/user";
import { Permission } from "../../../types/permission";
import { CongSettings } from "../../../types/scheduling/settings";
import { MapPoint, Territory, TerritoryAddress, TerritoryRecord } from "../../../types/scheduling/territory";
import User, { UserStatus } from "../../../types/user";
import { DropdownFilter } from "../../scheduling/common";
import { QueryClient, UseQueryOptions } from "@tanstack/react-query";
import QueryKeys from "../../../api/queryKeys";
import { getDayjs, stringToLocaleDate } from "../../../helpers/dateHelpers";
import { useCongSettings } from "../../../api/cong";
import { isMapboxURL, transformMapboxUrl } from "maplibregl-mapbox-request-transformer";

const mapboxAccessToken =
  import.meta.env.VITE_MAPBOX_ACCESS_TOKEN ||
  "pk.eyJ1IjoiY29uZ3NvZnQiLCJhIjoiY2x0bTR0cGZ5MWhyNjJpcDVqZ3B6NGx6OCJ9.c-WNCj8cEPnphpOB_zHRWQ";

export function terrLabel(terr: { number: string; locality: string }): string {
  // "number - locality" unless sortByLocality is enabled, then it's "locality - number"
  const numberTrimmed = terr.number.trim();
  const localityTrimmed = terr.locality.trim();
  const first = HourglassGlobals.whoamiSettings?.territory.sortByLocality ? localityTrimmed : numberTrimmed;
  const second = HourglassGlobals.whoamiSettings?.territory.sortByLocality ? numberTrimmed : localityTrimmed;

  if (first && second) {
    return `${first} — ${second}`;
  }

  return first || second;
}

export function formatTerritoryRecord(trec: TerritoryRecord, userMap: Map<number, User>): string {
  const publisher = userMap.get(trec.user_id);
  const start = trec.start ? stringToLocaleDate(trec.start) : "---";
  const end = trec.end ? stringToLocaleDate(trec.end) : "---";

  return `${start} | ${end} | ${publisher?.displayName ?? ""}`;
}

export function canUpdateTerritory(): boolean {
  return HourglassGlobals.permissions.has(Permission.UpdateTerritory);
}

export function canViewTerritory(): boolean {
  return HourglassGlobals.permissions.has(Permission.ViewTerritory);
}

const leadingNumberRegex = /^\s*(\d*)(\D*)\s*/;

export function territoryRecordCompare(a: TerritoryRecord, b: TerritoryRecord): number {
  return (a.start ?? 0) > (b.start ?? 0) ? 1 : -1;
}

export function territoryCompare(a: Territory, b: Territory): number {
  // split the number portion into the part that starts with digits, and the rest
  // the goal is to sort 1, 1A, 2, ... 10, 10A
  if (a.disabled && !b.disabled) return 1;
  if (!a.disabled && b.disabled) return -1;

  if (HourglassGlobals.whoamiSettings?.territory?.sortByLocality) {
    const localityCompare = CollatorSingleton.getInstance().compare(a.locality.trim(), b.locality.trim());
    if (localityCompare != 0) return localityCompare;
  }

  const aTrimmed = a.number.trim();
  const bTrimmed = b.number.trim();

  const aMatch = aTrimmed.match(leadingNumberRegex);
  const bMatch = bTrimmed.match(leadingNumberRegex);
  if (aMatch && bMatch && aMatch[1] && bMatch[1]) {
    const aInt = parseInt(aMatch[1]);
    const bInt = parseInt(bMatch[1]);
    const aStr = aMatch[2] ?? "";
    const bStr = bMatch[2] ?? "";
    if (!isNaN(aInt) && !isNaN(bInt)) {
      // we have two valid numbers. use them
      if (aInt !== bInt) return aInt - bInt;
      // they're the same. sort based on the text portion
      if (aStr !== bStr) return CollatorSingleton.getInstance().compare(aStr, bStr);
      // they are the same. sort based on the locality
      return CollatorSingleton.getInstance().compare(a.locality, b.locality);
    }
  }
  return CollatorSingleton.getInstance().compare(aTrimmed, bTrimmed);
}

export type PrintOptions = {
  excludeHideOnMapAddresses: boolean;
  excludeAllAddresses: boolean;
  dncOnly: boolean;
  invertedShading: boolean;
  mapFullPage: boolean;
};

export function addressesForPDF(addresses: TerritoryAddress[], options?: PrintOptions): TerritoryAddress[] {
  if (options?.excludeAllAddresses) return [];

  let locationCount = 0;
  return (
    addresses
      .filter((a) => {
        if (options?.dncOnly && !a.dnc) return false;
        return !(options?.excludeHideOnMapAddresses && a.hideOnMap);
      })
      .sort(territoryAddressCompare)
      .map((a) => {
        if (!a.location) return { ...a, pinLabel: undefined };
        locationCount++;
        return { ...a, pinLabel: `${locationCount}` };
      }) ?? []
  );
}

export const PublisherDDM = (props: {
  userMap: Map<number, User>;
  selectedUser: number | null;
  searchString: string;
  setSelectedUser: (set: number) => void;
  setSearchString: (set: string) => void;
  size?: "sm" | "lg";
  showClear?: boolean;
}) => {
  const [needSetFocus, setNeedSetFocus] = useState(false);

  return (
    <ButtonGroup className="dropdown-wide">
      <DropdownButton
        title={props.userMap.get(props.selectedUser || 0)?.displayName || t("general.none-selected")}
        variant="secondary"
        className="dropdown-bounded dropdown-wide"
        size={props.size}
        onToggle={(nextShow: boolean) => {
          if (!nextShow) props.setSearchString("");
          setNeedSetFocus(nextShow);
        }}
      >
        {props.showClear && !!props.selectedUser && (
          <>
            <Dropdown.Item onClick={() => props.setSelectedUser(0)}>
              ❌ <i>{t("schedules.fill.clear")}</i>
            </Dropdown.Item>
            <Dropdown.Divider />
          </>
        )}
        <DropdownFilter filter={props.searchString} setFilter={props.setSearchString} setFocus={needSetFocus} />
        {Array.from(props.userMap.values())
          .filter((u) => {
            if (u.id === props.selectedUser) return false;
            return containsFuzzy(u.displayName, props.searchString) && u.status !== UserStatus.StudentOnly;
          })
          .sort(userCompare)
          .map((u) => (
            <Dropdown.Item key={u.id} onClick={() => props.setSelectedUser(u.id)}>
              {u.displayName}
              {u.status === UserStatus.CO && (
                <div className="mb-1">
                  <Badge>{t("general.circuit-overseer")}</Badge>
                </div>
              )}
            </Dropdown.Item>
          ))}
      </DropdownButton>
    </ButtonGroup>
  );
};

export function useTerritoryMap() {
  const terrQuery = useTerritories();
  return useMemo(() => {
    if (!Array.isArray(terrQuery.data)) {
      return new Map<number, Territory>();
    }
    const terrMap = new Map<number, Territory>();
    terrQuery.data?.forEach((t) => {
      terrMap.set(t.id, t);
      t.children?.forEach((child) => terrMap.set(child.id, child));
    });
    return terrMap;
  }, [terrQuery.data]);
}

export type MapTileProvider = {
  url: string;
  attribution: string;
  isVector: boolean;
  skipCustomAttribution?: boolean;
};

export function tileStyle(provider: MapTileProvider): StyleSpecification | string {
  const rasterTileStyle = (prov: MapTileProvider): StyleSpecification => {
    return {
      version: 8,
      sources: {
        "raster-tiles": {
          type: "raster",
          tiles: [prov.url],
          tileSize: 256,
          attribution: prov.attribution,
        },
      },
      glyphs: maptilerGlyphs(),
      layers: [
        {
          id: "rasterLayer",
          type: "raster",
          source: "raster-tiles",
          minzoom: 0,
          maxzoom: 22,
        },
      ],
    };
  };

  return provider.isVector ? provider.url : rasterTileStyle(provider);
}

export function maptilerGlyphs(): string {
  return "https://api.maptiler.com/fonts/{fontstack}/{range}.pbf?key=" + maptilerApiKey();
}

function maptilerApiKey(): string {
  const devKey = import.meta.env.VITE_MAPTILER_KEY;
  return devKey ? devKey : "ZLfbPHyaiJoxOmcJHJZt";
}

export function mapFontStack(settings: CongSettings): string[] {
  if (settings.terr_tile_provider === 4) return ["Fira GO Map"];
  return ["Open Sans Semibold", "Arial Unicode MS Bold"];
}

export const annotationTextField: ExpressionSpecification = ["coalesce", ["get", "__gm_text"], ["get", "text"]];

export function hasAnnotationText(f: Feature): boolean {
  return !!f.properties?.text || !!f.properties?.__gm_text;
}

export function normalizeAnnotationText(f: Feature): Feature {
  if (f.properties?.__gm_text && !f.properties?.text) {
    return { ...f, properties: { ...f.properties, text: f.properties.__gm_text } };
  }
  return f;
}

export function tileProvider(settings: CongSettings): MapTileProvider {
  const mapTilerAttribution =
    '<a href="https://www.maptiler.com"><img src="https://api.maptiler.com/resources/logo.svg" alt="MapTiler Logo"/></a> <a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>';
  const mapTilerKey = maptilerApiKey();
  const year = new Date().getFullYear();
  const hereAttribution = `© ${year} HERE`;
  const esriAttribution = "Esri, Maxar, Earthstar Geographics, and the GIS User Community";
  const awsKey =
    import.meta.env.VITE_AWS_MAPS_KEY ||
    "v1.public.eyJqdGkiOiI0NjNlZGFkYS0yNzUwLTRlOTYtODE5Yi05YjFmNWM5NTRhOTEifS0N_ww6a5UuxFEUG-m-7ioVIL-kTWwtOd6uphugfE0KoiNuteTzZX1yyMyFrWaMmIxg0pl8p0sq0V7DE4xZLBLbPHelFc436eGrVWxfhVoRlLCkLVLaP0GXUQiBYHCeziNmbTML9oTWn2sn5vMTjqDoc9bLu3CfuEFNgD8WKRuy-fv89qd99Ds0W6QS6hebuvotFQ7j_93hbYAVwjLXCy-uGBSKT5ZSj_h-sj63353KvkcbY7CWvOU2-NhFbM2HnzVQ7kdgVKjz6QCnyutjjX8qxLtj-lfegaODOCZRv1GInJ80OAbZXL0V5cBCBiElANDk46MbgSYap3x76hMH-f0.ZWU0ZWIzMTktMWRhNi00Mzg0LTllMzYtNzlmMDU3MjRmYTkx";
  const mapboxAttribution =
    '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>';

  switch (settings.terr_tile_provider) {
    case 1:
      return {
        attribution:
          'Maps &copy; <a href="https://www.thunderforest.com" target="_blank">Thunderforest</a>, Data &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
        url: "https://tile.thunderforest.com/transport/{z}/{x}/{y}.png?apikey=52d4cb2673334b43ae5be1e59041afab",
        isVector: false,
      };
    case 2:
      return {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
        url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
        isVector: false,
      };
    case 3:
      return {
        attribution: mapTilerAttribution,
        url: `https://api.maptiler.com/maps/streets-v4/style.json?key=${mapTilerKey}`,
        isVector: true,
      };
    case 4:
      return {
        attribution: hereAttribution,
        url: `https://maps.geo.us-east-1.amazonaws.com/maps/v0/maps/here/style-descriptor?key=${awsKey}`,
        isVector: true,
      };
    case 5:
      return {
        attribution: mapTilerAttribution,
        url: `https://api.maptiler.com/maps/satellite-v4/style.json?key=${mapTilerKey}`,
        isVector: true,
      };
    case 6:
      return {
        attribution: esriAttribution,
        url: `https://maps.geo.us-east-1.amazonaws.com/maps/v0/maps/esriSatellite/style-descriptor?key=${awsKey}`,
        isVector: true,
      };
    case 7:
      return {
        attribution: mapTilerAttribution,
        url: `https://api.maptiler.com/maps/openstreetmap/style.json?key=${mapTilerKey}`,
        isVector: true,
      };
    case 8:
      return {
        attribution: mapTilerAttribution,
        url: `https://api.maptiler.com/maps/hybrid-v4/style.json?key=${mapTilerKey}`,
        isVector: true,
      };
    case 9:
      return {
        attribution: mapboxAttribution,
        url: `https://api.mapbox.com/styles/v1/mapbox/streets-v12?access_token=${mapboxAccessToken}`,
        isVector: true,
        skipCustomAttribution: true,
      };
    case 10:
      return {
        attribution: mapboxAttribution,
        url: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12?access_token=${mapboxAccessToken}`,
        isVector: true,
        skipCustomAttribution: true,
      };
    case 999:
      return {
        attribution: settings.terr_custom_tile_provider_attribution ?? "",
        url: settings.terr_custom_tile_provider_style_url ?? "",
        isVector: true,
      };
    default:
      return {
        attribution: mapTilerAttribution,
        url: `https://api.maptiler.com/maps/base-v4/style.json?key=${mapTilerKey}`,
        isVector: true,
      };
  }
}

enum TerritoryStatus {
  NotCheckedOut = "not_checked_out",
  CheckedOut = "checked_out",
  DueSoon = "due_soon",
  Overdue = "overdue",
}

type TerritoryColors = {
  fillColor: string;
  strokeColor: string;
};

function getTerritoryStatus(
  territoryId: number,
  recordsMap: Map<number, TerritoryRecord>,
  checkoutDurationDays: number,
  dueSoonThresholdDays: number = 14,
): TerritoryStatus {
  const record = recordsMap.get(territoryId);

  // Not checked out if no record or has end date
  if (!record || !record.start || record.end !== null) {
    return TerritoryStatus.NotCheckedOut;
  }

  // Calculate days since checkout
  const startDate = getDayjs(record.start);
  const currentDate = getDayjs(new Date());
  const daysSinceCheckout = currentDate.diff(startDate, "days");
  const daysUntilDue = checkoutDurationDays - daysSinceCheckout;

  if (daysUntilDue < 0) {
    return TerritoryStatus.Overdue;
  }

  if (daysUntilDue <= dueSoonThresholdDays) {
    return TerritoryStatus.DueSoon;
  }

  return TerritoryStatus.CheckedOut;
}

function getColorsForStatus(status: TerritoryStatus): TerritoryColors {
  switch (status) {
    case TerritoryStatus.CheckedOut:
      return {
        fillColor: "rgba(0,128,0,0.4)",
        strokeColor: "rgba(0,128,0,0.6)",
      };
    case TerritoryStatus.NotCheckedOut:
      return {
        fillColor: "rgba(0,102,255,0.4)",
        strokeColor: "rgba(0,102,255,0.6)",
      };
    case TerritoryStatus.DueSoon:
      return {
        fillColor: "rgba(255,193,7,0.4)",
        strokeColor: "rgba(255,193,7,0.6)",
      };
    case TerritoryStatus.Overdue:
      return {
        fillColor: "rgba(220,53,69,0.4)",
        strokeColor: "rgba(220,53,69,0.6)",
      };
  }
}

export type LabeledLayer = {
  label: string;
  geojson: Polygon | MultiPolygon;
  id: number;
  fillColor?: string;
  strokeColor?: string;
  center?: Position;
};

export function useAllMappedTerritories(
  filter?: (t: Territory) => boolean,
  options?: Partial<UseQueryOptions<Territory[], Error>>,
): LabeledLayer[] {
  const allTerritoriesQuery = useTerritories(options);
  const settingsQuery = useCongSettings();
  const territoryRecordsQuery = useTerritoryRecords(true); // latestOnly = true

  // Create a map of territory_id -> latest TerritoryRecord for O(1) lookup
  const recordsMap = useMemo(() => {
    const map = new Map<number, TerritoryRecord>();
    if (territoryRecordsQuery.data) {
      territoryRecordsQuery.data.forEach((record) => {
        map.set(record.territory_id, record);
      });
    }
    return map;
  }, [territoryRecordsQuery.data]);

  const checkoutDurationDays = settingsQuery.data?.terr_checkout_duration_days ?? 120;
  const dueSoonThresholdDays = 14;

  // Memoized so callers that don't pass a fresh `filter` every render (e.g. useAllMappedTerritories()
  // with no args) get a stable array reference across unrelated re-renders, instead of a new array (and
  // therefore a cache-busted downstream useMemo) every time this component re-renders for any reason.
  return useMemo(() => {
    return allTerritoriesQuery.data
      ? allTerritoriesQuery.data
          .filter((t): t is Territory & { boundaries: string } => !!t.boundaries)
          .filter((t) => (filter ? filter(t) : true))
          .map((t) => {
            const status = getTerritoryStatus(t.id, recordsMap, checkoutDurationDays, dueSoonThresholdDays);
            const colors = getColorsForStatus(status);
            const geojson = JSON.parse(t.boundaries) as MultiPolygon;

            let center: Position | undefined;
            try {
              center = cachedPolygonCenter(getPolygon(geojson), t.id.toString(), t.boundaries);
            } catch {
              // polylabel can fail on degenerate polygons
            }

            return {
              label: t.number,
              geojson,
              id: t.id,
              fillColor: colors.fillColor,
              strokeColor: colors.strokeColor,
              center,
            };
          })
      : [];
  }, [allTerritoriesQuery.data, filter, recordsMap, checkoutDurationDays, dueSoonThresholdDays]);
}

export enum TerritoryPage {
  Checkout,
  Return,
  Records,
  Territories,
  CongregationTerritory,
  Import,
  Statistics,
  Addresses,
  Tags,
  Transfer,
}

export type TerritoryOption = {
  id: number;
  label: string;
};

export type TerritoryAddressWithLocation = TerritoryAddress & { location: NonNullable<MapPoint> };

export function locationsForTerritory(
  geojson: MultiPolygon,
  addresses: TerritoryAddressWithLocation[],
): TerritoryAddress[] {
  return addresses.filter((a) =>
    geojson.coordinates.some((polygon) => {
      if (Array.isArray(polygon)) return polygon.some((c) => pointInPolygon([a.location.x, a.location.y], c));
      return false;
    }),
  );
}

function territoryGeoJSONProperties(territory: Territory): GeoJsonProperties {
  return {
    number: territory.number,
    locality: territory.locality,
    business: territory.business,
    notes: territory.notes,
    url: territory.url,
    disabled: territory.disabled,
    tags: territory.tags,
  };
}

function territoryGeoJSONPropertiesSimple(territory: Territory): GeoJsonProperties {
  return {
    number: territory.number,
    locality: territory.locality,
    notes: territory.notes,
  };
}

export function territoryAsGeoJSON(territory: Territory): FeatureCollection {
  const features: Feature[] = [];

  if (territory.boundaries) {
    const boundary: MultiPolygon = JSON.parse(territory.boundaries);
    const feature: Feature<MultiPolygon> = {
      type: "Feature",
      id: territory.id,
      geometry: {
        type: "MultiPolygon",
        coordinates: boundary.coordinates,
      },
      properties: territoryGeoJSONProperties(territory),
    };

    features.push(feature);
  }

  if (territory.annotations) {
    const annotations: FeatureCollection = JSON.parse(territory.annotations);
    if (Array.isArray(annotations.features) && annotations.features.length > 0) {
      if (!territory.boundaries && annotations.features[0]) {
        annotations.features[0].id = territory.id;
        annotations.features[0].properties = {
          ...annotations.features[0].properties,
          ...territoryGeoJSONProperties(territory),
        };
      }

      features.push(...annotations.features);
    }
  }

  return {
    type: "FeatureCollection",
    features: features,
  };
}

export function territoryAsGeoJSONSimple(territory: Territory): FeatureCollection {
  const features: Feature[] = [];

  if (territory.boundaries) {
    const boundary: MultiPolygon = JSON.parse(territory.boundaries);
    const feature: Feature<MultiPolygon> = {
      type: "Feature",
      geometry: {
        type: "MultiPolygon",
        coordinates: boundary.coordinates,
      },
      properties: territoryGeoJSONPropertiesSimple(territory),
    };

    features.push(feature);
  }

  if (territory.annotations) {
    const annotations: FeatureCollection = JSON.parse(territory.annotations);
    if (Array.isArray(annotations.features) && annotations.features.length > 0) {
      if (!territory.boundaries && annotations.features[0]) {
        annotations.features[0].properties = {
          ...annotations.features[0].properties,
          ...territoryGeoJSONPropertiesSimple(territory),
        };
      }

      features.push(...annotations.features);
    }
  }

  return {
    type: "FeatureCollection",
    features: features,
  };
}

export function territoryFilename(territory: Territory): string {
  const label = terrLabel(territory).replace(/[^a-z0-9]+/gi, "_");
  return `${t("schedules.territory.territory").toLocaleLowerCase()}_${label}`;
}

export async function invalidateTerritoriesAfterImport(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.Territories],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.TerritoryAddresses],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.TerritoryRecordsLatest],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.TerritoryRecordsAll],
    }),
  ]);
}

export function territoriesWithChildren(territories: Territory[]): Territory[] {
  const allTerritories = [...territories];
  territories.forEach((territory) => territory.children?.forEach((c) => allTerritories.push(c)));
  return allTerritories;
}

export function importLanguage(lang?: string): string | undefined {
  const lcLang = lang?.trim().toLowerCase();
  if (!lcLang) return;
  switch (lcLang) {
    case "spanish":
      return "es";
    case "english":
      return "en";
    case "french":
      return "fr";
    case "portuguese":
      return "pt";
    case "italian":
      return "it";
    case "chinese":
      return "zh";
    case "vietnamese":
      return "vi";
    case "american sign language (asl)":
      return "ase";
    case "filipino":
      return "fil";
    case "arabic":
      return "ar";
    case "japanese":
      return "ja";
    case "hindi":
      return "hi";
    case "russian":
      return "ru";
  }
}

export function addressesGeoJSON(
  addresses: TerritoryAddress[],
  filter?: (a: TerritoryAddress) => boolean,
): FeatureCollection {
  const features =
    addresses
      ?.filter((a) => {
        if (!(a.location?.x && a.location?.y)) return false;
        if (filter) return filter(a);
        return true;
      })
      .map((a) => {
        // Only include text property if pinLabel has actual content
        const properties: GeoJsonProperties = { id: a.id };
        if (a.pinLabel && a.pinLabel.trim() !== "") {
          properties.text = a.pinLabel;
        }
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [a.location?.x ?? 0, a.location?.y ?? 0],
          },
          properties: properties,
        } as Feature;
      }) ?? [];

  return {
    type: "FeatureCollection",
    features: features,
  };
}

export function transformMapboxRequest(url: string, resourceType?: ResourceType) {
  if (isMapboxURL(url)) {
    return transformMapboxUrl(url, resourceType, mapboxAccessToken);
  }
  return { url };
}
